import { useCallback, useEffect, useState } from "react";
import { Braces, ChevronDown, ChevronRight, Loader2, RefreshCw, Send, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { getFoundationJobSpPreflight, pushFoundationJobSp, getFoundationJobSpLog } from "@/lib/api";
import FoundationSpConfirmDialog from "./FoundationSpConfirmDialog";
import FoundationSpResultCard from "./FoundationSpResultCard";
import FoundationJobSpLog from "./FoundationJobSpLog";
import { CollapsibleSection } from "@/components/settings/SettingsUI";

const TEST_RE = /^TEST-\d{5}$/;

function Fact({ label, value, testId }) {
  return (
    <div className="text-xs">
      <div className="text-slate-500">{label}</div>
      <code className="text-slate-800" data-testid={testId}>{value ?? "—"}</code>
    </div>
  );
}

// Standalone admin test screen for sp_insert_update_jobs (Phase 1).
export default function FoundationJobSpPanel() {
  const [pf, setPf] = useState(null);
  const [pfLoading, setPfLoading] = useState(false);
  const [showParams, setShowParams] = useState(false);
  const [jobNo, setJobNo] = useState("TEST-");
  const [desc, setDesc] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [logKey, setLogKey] = useState(0);

  const loadPreflight = useCallback(async () => {
    setPfLoading(true);
    try { const { data } = await getFoundationJobSpPreflight(); setPf(data); }
    catch { setPf({ ok: false, status: "error", blockers: ["Preflight request failed"], parameters: [] }); }
    finally { setPfLoading(false); }
  }, []);

  useEffect(() => { loadPreflight(); }, [loadPreflight]);

  const jobNoValid = TEST_RE.test(jobNo);
  const descTrimmed = desc.trim();
  const descSent = descTrimmed.slice(0, 30);
  const truncated = descTrimmed.length > 30;
  const canPush = !!pf?.ok && jobNoValid && descTrimmed.length > 0 && !busy;

  const doPush = async () => {
    setConfirmOpen(false);
    setBusy(true);
    setResult(null);
    try {
      const { data } = await pushFoundationJobSp(jobNo, descTrimmed);
      setResult(data);
      if (data.outcome === "success") toast.success(data.message);
      else toast.error(data.message);
      setLogKey((k) => k + 1);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Push request failed";
      setResult({ outcome: "bridge_error", message: detail, exception_text: detail, job_no: jobNo, description: descSent, truncated });
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <CollapsibleSection
      id="foundation-job-sp" testId="foundation-job-sp-section"
      icon={Braces} iconClass="text-violet-600"
      title="Jobs (SP) — sp_insert_update_jobs · Phase 1 test screen"
      extra={pf && (
            <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full border ${
              pf.ok ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-rose-700 bg-rose-50 border-rose-200"}`}
                  data-testid="job-sp-preflight-badge">
              {pf.ok ? <ShieldCheck className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
              Preflight {pf.ok ? "OK" : (pf.status === "blocked" ? "blocked" : "error")}
            </span>
          )}
      actions={
        <button onClick={loadPreflight} disabled={pfLoading}
                className="text-[11px] text-violet-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="btn-job-sp-preflight">
          <RefreshCw className={`w-3 h-3 ${pfLoading ? "animate-spin" : ""}`} /> {pfLoading ? "Checking…" : "Re-run preflight"}
        </button>
      }
      defaultOpen={false}
    >
      <p className="text-xs text-slate-500 mb-2">
        Executes <code>dbo.sp_insert_update_jobs</code> with exactly four parameters over the existing bridge.
        Runs only against <code>Cas_16075_Practice</code>; anything else is refused before the call.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 mb-2"
           data-testid="job-sp-header">
        <Fact label="Target Database" value={pf?.database} testId="job-sp-db" />
        <Fact label="Login" value={pf?.login} testId="job-sp-login" />
        <Fact label="company_no" value={pf?.company_no} testId="job-sp-company" />
        <Fact label="EXECUTE Granted" value={pf ? (pf.can_execute ? "yes" : "no") : null} testId="job-sp-exec" />
      </div>

      {pf?.blockers?.length > 0 && (
        <div className="space-y-1 mb-2" data-testid="job-sp-blockers">
          {pf.blockers.map((b, i) => (
            <div key={i} className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg px-3 py-2 text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{b}</span>
            </div>
          ))}
        </div>
      )}
      {pf?.advisories?.length > 0 && (
        <div className="space-y-1 mb-2" data-testid="job-sp-advisories">
          {pf.advisories.map((a, i) => (
            <div key={i} className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{a}</span>
            </div>
          ))}
        </div>
      )}

      {pf?.parameters?.length > 0 && (
        <div className="mb-3">
          <button onClick={() => setShowParams((s) => !s)}
                  className="text-xs text-slate-600 hover:underline inline-flex items-center gap-1"
                  data-testid="btn-job-sp-toggle-params">
            {showParams ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {pf.parameters.length} parameters from sys.parameters
            {pf.undefaulted_extra_params?.length > 0 && ` · ${pf.undefaulted_extra_params.length} outside Phase 1 report no default`}
          </button>
          {showParams && (
            <div className="mt-1 max-h-64 overflow-auto rounded-lg border border-slate-200" data-testid="job-sp-params-table">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50 text-slate-500 text-left sticky top-0">
                  <tr>
                    <th className="px-2 py-1">name</th><th className="px-2 py-1">type</th>
                    <th className="px-2 py-1">max_length</th><th className="px-2 py-1">is_output</th>
                    <th className="px-2 py-1">has_default_value</th>
                  </tr>
                </thead>
                <tbody>
                  {pf.parameters.map((p) => {
                    const phase1 = pf.phase1_params?.includes(p.name);
                    return (
                      <tr key={p.name} className={`border-t border-slate-100 ${phase1 ? "bg-violet-50 font-medium" : ""}`}>
                        <td className="px-2 py-1 font-mono">{p.name}</td>
                        <td className="px-2 py-1">{p.type_name}</td>
                        <td className="px-2 py-1">{p.max_length}</td>
                        <td className="px-2 py-1">{p.is_output ? "yes" : "no"}</td>
                        <td className="px-2 py-1">{p.has_default_value ? "yes" : "no"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-[180px_1fr_auto] gap-2 items-end">
        <label className="text-xs text-slate-600">
          Job Number <span className="text-slate-400">(TEST-#####)</span>
          <input value={jobNo} onChange={(e) => setJobNo(e.target.value.toUpperCase())} maxLength={10}
                 className={`mt-1 w-full border rounded-md px-2 py-1.5 text-sm font-mono ${jobNo && !jobNoValid ? "border-rose-300" : "border-slate-300"}`}
                 data-testid="job-sp-input-jobno" />
        </label>
        <label className="text-xs text-slate-600">
          Description <span className="text-slate-400">({descSent.length}/30{truncated ? " — will be truncated" : ""})</span>
          <input value={desc} onChange={(e) => setDesc(e.target.value)}
                 className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                 data-testid="job-sp-input-desc" />
        </label>
        <button onClick={() => setConfirmOpen(true)} disabled={!canPush}
                className="bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white rounded-md text-sm px-3 py-2 inline-flex items-center gap-1"
                data-testid="btn-job-sp-push">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {busy ? "Pushing…" : "Push to Foundation"}
        </button>
      </div>
      {jobNo && !jobNoValid && (
        <div className="text-[11px] text-rose-600 mt-1" data-testid="job-sp-jobno-error">
          Job number must match TEST-##### (exactly 10 characters).
        </div>
      )}

      <FoundationSpConfirmDialog
        open={confirmOpen} onOpenChange={setConfirmOpen} onConfirm={doPush} testIdPrefix="job-sp"
        preview={{ job_no: jobNo, description: descSent, truncated, database: pf?.database || "Cas_16075_Practice" }}
      />

      {result && <div className="mt-3"><FoundationSpResultCard result={result} testIdPrefix="job-sp" /></div>}

      <FoundationJobSpLog refreshKey={logKey} loader={getFoundationJobSpLog} />
    </CollapsibleSection>
  );
}
